import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { Product } from '@/services/productService';
import { useNavigationType } from 'react-router-dom';

const visitedGroups = new Set<string>();
const ProductCard = lazy(() => import('@/components/ProductCard'));

function isRestoringGroup(group: string) {
  try {
    const key = window.history.state?.key ?? 'default';
    const saved = new Map(JSON.parse(sessionStorage.getItem('umasaura:route-scroll:v1') || '[]')).get(key) as { anchor?: { sectionId?: string } } | undefined;
    return saved?.anchor?.sectionId === group;
  } catch { return false; }
}

/** Keep distant card hooks out of the first render; preserve visited grids on Back. */
export function HomeProductGrid({ products, group, searching }: { products: Product[]; group: string; searching: boolean }) {
  const element = useRef<HTMLDivElement>(null);
  const navigationType = useNavigationType();
  // A discarded tab has no in-memory visited set. Mount its saved target now
  // so scroll restoration never has to wait for that distant grid to enter view.
  const [ready, setReady] = useState(() => visitedGroups.has(group) || (navigationType === 'POP' && isRestoringGroup(group)));
  useEffect(() => {
    if (ready || searching) { visitedGroups.add(group); return; }
    const node = element.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') { setReady(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { visitedGroups.add(group); setReady(true); observer.disconnect(); }
    }, { rootMargin: '800px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [group, ready, searching]);
  return <div ref={element} aria-busy={!ready && !searching} className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
    {ready || searching ? <Suspense fallback={products.map(product => <div key={product.id} className="min-h-[320px] rounded-xl bg-gray-50" aria-hidden="true" />)}>{products.map(product => <ProductCard key={product.id} {...product} />)}</Suspense> : products.map(product => <div key={product.id} aria-hidden="true" className="overflow-hidden rounded-xl bg-gray-50 shadow-md">
      <div className="m-3 aspect-square rounded-2xl bg-stone-100 md:m-4" />
      <div className="h-[146px] md:h-[160px]" />
    </div>)}
  </div>;
}
